/**
 * Provider metadata + live model discovery for the AI-profile form.
 * Web port of the v3 settings module's fetchModelsForProvider — asks each
 * provider which models the entered key can actually use, falling back to
 * the static lists when offline or the key is bad.
 */

export interface ProviderMeta {
  label: string;
  color: string;
  defaultModel: string;
  keyHint: string;
  models: string[];
}

export const AI_PROVIDERS: Record<string, ProviderMeta> = {
  gemini: {
    label: 'Google Gemini', color: '#4285f4', defaultModel: 'gemini-2.0-flash',
    keyHint: 'Get a key at aistudio.google.com',
    models: ['gemini-2.0-flash', 'gemini-1.5-flash', 'gemini-1.5-pro', 'gemini-2.5-pro'],
  },
  anthropic: {
    label: 'Anthropic Claude', color: '#cc785c', defaultModel: 'claude-3-5-haiku-20241022',
    keyHint: 'Get a key at console.anthropic.com',
    models: ['claude-3-5-sonnet-20241022', 'claude-3-5-haiku-20241022', 'claude-3-opus-20240229'],
  },
  openai: {
    label: 'OpenAI ChatGPT', color: '#10a37f', defaultModel: 'gpt-4o-mini',
    keyHint: 'Get a key at platform.openai.com',
    models: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'o1-mini'],
  },
  ollama: {
    label: 'Ollama (local)', color: '#0a8f6b', defaultModel: 'gemma4:12b',
    keyHint: 'Base URL of your local Ollama service — e.g. http://localhost:11434',
    models: ['gemma4:12b', 'gemma4:4b', 'qwen2.5-coder:7b', 'llama3.1:8b'],
  },
};

/** Profiles store the base URL in the same field other providers use for the key. */
export const OLLAMA_DEFAULT_BASE_URL = 'http://localhost:11434';

function normalizeOllamaBaseUrl(raw: string): string {
  let url = raw.trim() || OLLAMA_DEFAULT_BASE_URL;
  if (!/^https?:\/\//i.test(url)) url = `http://${url}`;
  return url.replace(/\/+$/, '');
}

export interface ModelDiscoveryResult {
  models: string[];
  /** Set when the live fetch failed and `models` is the static fallback. */
  warn?: string;
}

export async function fetchModelsForProvider(provider: string, key: string): Promise<ModelDiscoveryResult> {
  const fallback = AI_PROVIDERS[provider]?.models ?? [];
  try {
    if (provider === 'gemini') {
      const res = await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${encodeURIComponent(key)}`);
      const data = await res.json() as {
        error?: { message?: string };
        models?: Array<{ name: string; supportedGenerationMethods?: string[] }>;
      };
      if (data.error) return { models: fallback, warn: data.error.message ?? 'API error' };
      const models = (data.models ?? [])
        .filter((m) => (m.supportedGenerationMethods ?? []).includes('generateContent'))
        .map((m) => m.name.replace('models/', ''));
      return { models: models.length ? models : fallback };
    }
    if (provider === 'anthropic') {
      // ?limit=100 — the endpoint pages at 20 by default, which silently
      // hides older models from the picker.
      const res = await fetch('https://api.anthropic.com/v1/models?limit=100', {
        headers: {
          'x-api-key': key,
          'anthropic-version': '2023-06-01',
          // Required for browser-direct calls (same as the chat endpoint):
          'anthropic-dangerous-direct-browser-access': 'true',
        },
      });
      const data = await res.json() as { error?: { message?: string }; data?: Array<{ id: string }> };
      if (!res.ok) return { models: fallback, warn: data.error?.message ?? 'API error' };
      const models = (data.data ?? []).map((m) => m.id);
      return { models: models.length ? models : fallback };
    }
    if (provider === 'openai') {
      const res = await fetch('https://api.openai.com/v1/models', {
        headers: { 'Authorization': `Bearer ${key}` },
      });
      const data = await res.json() as { error?: { message?: string }; data?: Array<{ id: string }> };
      if (!res.ok) return { models: fallback, warn: data.error?.message ?? 'API error' };
      const models = (data.data ?? [])
        .filter((m) => /^(gpt-4|gpt-3\.5|gpt-5|o1|o3|o4|chatgpt)/.test(m.id) &&
                       !/(instruct|search|realtime|audio|transcribe|tts|image)/.test(m.id))
        .map((m) => m.id).sort();
      return { models: models.length ? models : fallback };
    }
    if (provider === 'ollama') {
      // 'key' field carries the base URL for ollama profiles.
      const base = normalizeOllamaBaseUrl(key);
      // A secure public page can't reach a local address — say so plainly
      // instead of letting fetch fail with a vague "Could not reach Ollama".
      if (typeof window !== 'undefined') {
        const targetIsLocal = /^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0)(:|\/|$)/i.test(base);
        const pageIsRemoteHttps = window.location.protocol === 'https:' &&
          !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);
        if (targetIsLocal && pageIsRemoteHttps) {
          return { models: fallback,
                   warn: `this HTTPS site can't reach ${base} — open the app at http://localhost, use a tunnel, or generate from the Android app` };
        }
      }
      const res = await fetch(`${base}/v1/models`);
      if (!res.ok) {
        return { models: fallback, warn: `Ollama responded ${res.status} — is the service running?` };
      }
      const data = await res.json() as { data?: Array<{ id: string }> };
      const models = (data.data ?? []).map((m) => m.id).sort();
      return { models: models.length ? models : fallback,
               warn: models.length ? undefined : 'No models installed — run `ollama pull gemma4:12b`.' };
    }
  } catch {
    if (provider === 'ollama') {
      return { models: fallback, warn: 'Could not reach Ollama — is the service running locally?' };
    }
    return { models: fallback, warn: 'Network error — showing the built-in list' };
  }
  return { models: fallback };
}
